import { isValidElement, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '../../../utils/cx';

type ItemCardSize = 'standard' | 'compact';

type Badge = ReactNode;

const placeholderStyles =
  'h-full w-full bg-[var(--gn-color-gray-warm-100)] text-transparent';

export interface ItemCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Primary text displayed on the card */
  title: ReactNode;
  /** Supporting text below the title */
  subtitle?: ReactNode;
  /** Optional custom media node rendered inside the thumbnail */
  media?: ReactNode;
  /** Convenience props to render an image if `media` is not provided */
  imageSrc?: string;
  imageAlt?: string;
  /** Optional badges or pill labels rendered beneath the text */
  badges?: Badge[];
  /** Layout size. Standard stacks content vertically while compact keeps content in a row */
  size?: ItemCardSize;
}

const badgeClass =
  'flex items-center justify-center rounded-md border border-[#D7D3D0] bg-white px-2 py-[3px] text-xs font-medium text-[#44403C]';

export function ItemCard({
  title,
  subtitle,
  media,
  imageSrc,
  imageAlt,
  badges,
  children,
  size = 'standard',
  className,
  ...props
}: ItemCardProps) {
  const mediaNode =
    media ??
    (imageSrc ? (
      <img
        src={imageSrc}
        alt={imageAlt ?? ''}
        className="size-full object-cover"
      />
    ) : (
      <div className={placeholderStyles} aria-hidden />
    ));

  const mediaWrapper = (
    <div
      className={cx(
        'overflow-hidden rounded-[24px] bg-[var(--gn-color-gray-warm-50)]',
        size === 'compact' ? 'aspect-[132/152]' : 'aspect-[216/249]'
      )}
    >
      {mediaNode}
    </div>
  );

  const badgesMarkup =
    badges && badges.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {badges.map((badge, index) => {
          if (typeof badge === 'string') {
            return (
              <span className={badgeClass} key={`${badge}-${index}`}>
                {badge}
              </span>
            );
          }

          if (isValidElement(badge)) {
            return (
              <span className="inline-flex" key={badge.key ?? index}>
                {badge}
              </span>
            );
          }

          return (
            <span className={badgeClass} key={`badge-${index}`}>
              {badge}
            </span>
          );
        })}
      </div>
    ) : null;

  return (
    <div
      {...props}
      className={cx(
        'flex flex-col',
        size === 'compact' ? 'gap-3' : 'gap-4',
        className
      )}
    >
      {size === 'compact' ? (
        <div className="flex flex-col gap-3">
          {mediaWrapper}
          <div>
            <p className="text-lg font-bold text-gray-900">{title}</p>
            {subtitle && (
              <p className="mt-1 text-base text-slate-500">{subtitle}</p>
            )}
          </div>
          {children}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {mediaWrapper}
          <div>
            <p className="text-lg font-bold text-gray-900">{title}</p>
            {subtitle && (
              <p className="mt-1 text-base text-slate-500">{subtitle}</p>
            )}
          </div>
          {badgesMarkup}
          {children}
        </div>
      )}
    </div>
  );
}
